import { API_BASE_URL, deleteRequest, get, handleResponse, patch, postJson, put } from './apiClient';
import { getAuthHeader } from './authSession';
import { notifyTrailChanged } from './rfqLifecycleApi';
import { emailRefusalMessage } from './emailRefusal';
import { roundQuantity } from '../utils/packConversion';
import type { DialogContent } from '../utils/errorPresentation';

/**
 * A PO line restated in the supplier's own pack (D82). Server-owned: the client
 * reads it and never sends it back (PUT ignores it).
 *
 * - `applied`: the pieces divided evenly, the line was written in packs at generation.
 * - `pending`: they did not; the line is still in pieces and staff must choose
 *   up / down / a number of packs before the PO can be sent or exported.
 * - `decided`: staff chose (`decision`).
 * Absent on POs raised before D82 — read as 'none'.
 */
export type PackConversionStatus = 'none' | 'applied' | 'pending' | 'decided';
export type PackRoundingDecision = 'up' | 'down' | 'manual';

export interface VendorPoPackConversion {
  status: PackConversionStatus;
  /** The supplier's pack, e.g. 'CS'. */
  packUnit: string | null;
  /** e.g. 25. */
  piecesPerPack: number | null;
  /** The pieces asked for (after stock netting), e.g. 110. */
  requestedQuantity: number | null;
  /** e.g. 'PCS'. */
  requestedUom: string | null;
  decision: PackRoundingDecision | null;
  /** Where the pack came from: the supplier's invoice history or the catalogue row; null = none / older PO. */
  source?: 'history' | 'catalogue' | null;
  /** D119.4: `source` in words, for the pack prompt's "Pack from: ..."; null / absent on a line written before it. */
  sourceNote?: string | null;
  /** D166: the pack in the catalogue row's words ("CASE of 4 x 4 L"), display only; null / absent when none is stated. */
  packText?: string | null;
}

export interface VendorPurchaseOrderItemDto {
  lineNumber?: number;
  /** The order line's own (RFQ) wording. Internal readers match on it. */
  itemName: string;
  /**
   * D119.8: what the supplier calls the product (the PO vendor's matched catalogue row's name);
   * the supplier's documents print it, else `itemName`. Null / absent = none. Staff edit it on the PO page.
   */
  supplierItemName?: string | null;
  itemDescription?: string;
  itemCode?: string;
  quantity: number;
  uom?: string;
  unitPrice?: number | null;
  totalPrice?: number | null;
  currency?: string;
  unitPriceUsd?: number | null;
  totalPriceUsd?: number | null;
  remarks?: string;
  packConversion?: VendorPoPackConversion;
  /** Which rung of the D82 price ladder priced this line; null = no price. */
  priceSource?: 'supplier_catalogue' | 'quotation_cost' | 'manual' | null;
  /** The VAT percent that applies to this before-VAT price (D82; 0 = none); null = nothing to add. Server-owned. */
  vatPercent?: number | null;
  /** `_id` of the confirmed-order line this PO line was raised from; null on legacy lines. Server-owned. */
  orderLineId?: string | null;
  /**
   * D81: the order line left (or has nothing left to buy) after the PO was sent — the line
   * stays on the amended PO at 0 so the supplier sees it went, out of the totals. Server-owned.
   */
  removed?: boolean;
}

/**
 * 'amended' (D81): changed since it was sent — the order's quantities moved, its supplier changed,
 * or staff edited what its copy prints (D170.3) — and needs re-sending; it is with the supplier
 * all the same. 'billed' (D144): every line is billed in
 * full, and the invoice that made it so has been confirmed — still with the supplier, same as
 * 'amended'. 'cancelled': a draft whose every line left.
 */
export type VendorPoStatus = 'draft' | 'sent' | 'amended' | 'billed' | 'cancelled';

export interface VendorPurchaseOrderVendorDto {
  _id: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface ConfirmedOrderRef {
  _id: string;
  rfqNumber?: string;
  quotationNumber?: string;
  vesselName?: string;
  clientName?: string;
  confirmedAt?: string;
  /** The client's own PO reference (B6), carried from ConfirmedOrder.clientPoNumber. */
  clientPoNumber?: string;
}

/** The subset of a Quotation's own terms the PO detail header reads (never retyped onto the PO). */
export interface QuotationTermsRef {
  paymentTerms?: string;
}

export interface VendorPurchaseOrderDto {
  _id: string;
  poNumber: string;
  /** Original vessel name used in the PO number prefix; preserved for strip/restore. */
  vesselName?: string;
  /** When true, hide the vessel prefix in display, the PDF / Excel, and outbound emails. */
  hideVesselPrefix?: boolean;
  /** D119.7: one note to the supplier for the whole PO, printed on the PDF / Excel. */
  generalRemarks?: string;
  confirmedOrderId?: ConfirmedOrderRef | string;
  rfqId?: { _id: string; rfqId?: string; customerName?: string; vesselName?: string } | string;
  quotationId?: string | { _id?: string; quotationNumber?: string; terms?: QuotationTermsRef };
  vendorId?: VendorPurchaseOrderVendorDto | string | null;
  vendorName: string;
  vendorEmail?: string;
  vendorAddress?: string;
  vendorGstin?: string;
  vendorVat?: string;
  vendorPhone?: string;
  totalItems: number;
  totalPrice: number;
  /**
   * 'before_vat' = the PO was generated or rebuilt under D82 and its prices are
   * the supplier's pre-VAT prices. null/absent = stored before D82: its prices
   * are the VAT-inclusive quotation cost (D63). Read it; never infer it.
   */
  priceBasis?: 'before_vat' | null;
  /**
   * How many of `items` have no agreed price yet and so are NOT included in
   * totalPrice above (D54 leaves a price-list line's cost null until a supplier
   * is picked — a normal state, not a data gap; TECH_DEBT #342). Every screen
   * that prints totalPrice reads this alongside it — see `unpricedLineNote`.
   */
  unpricedLineCount?: number;
  currency?: string;
  /** TTD per 1 USD when PO was generated */
  exchangeRateUsdToTtd?: number | null;
  status: string;
  /** D81: 0 until the PO is changed after a send, +1 per amendment. */
  revision?: number;
  /** D81: the revision last sent to the supplier; null = never sent. */
  sentRevision?: number | null;
  createdAt: string;
  /** Expected delivery / vessel ETA — user-entered on the PO. */
  expectedDeliveryDate?: string | null;
  items: VendorPurchaseOrderItemDto[];
  sentAt?: string;
  sentBy?: string;
  sentTo?: string[];
}

export interface VendorPurchaseOrderListResponse {
  success: boolean;
  data: {
    vendorPurchaseOrders: VendorPurchaseOrderDto[];
    /**
     * How many POs across the WHOLE filtered set (every page, not just the one
     * loaded) have no vendor record — server-computed
     * (`vendorPurchaseOrder.service.countUnresolvedVendorPos`) under the same
     * filter this response's rows were queried with, so a "N purchase orders
     * cannot be sent" banner can state the real number for the current
     * search/status/date filter rather than only what is on screen.
     */
    unresolvedVendorCount: number;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface VendorPoListFilters {
  status?: string;      // 'all' | VendorPoStatus
  sortBy?: string;      // 'date' | 'amount' | 'qty' | 'vendorName' | 'vessel' | 'status'
  sortDir?: 'asc' | 'desc';
  createdFrom?: string; // YYYY-MM-DD
  createdTo?: string;   // YYYY-MM-DD
}

export async function fetchVendorPurchaseOrders(page = 1, limit = 100, search = '', filters: VendorPoListFilters = {}) {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);
  if (filters.status && filters.status !== 'all') query.set('status', filters.status);
  if (filters.sortBy) query.set('sortBy', filters.sortBy);
  if (filters.sortDir) query.set('sortDir', filters.sortDir);
  if (filters.createdFrom) query.set('createdFrom', filters.createdFrom);
  if (filters.createdTo) query.set('createdTo', filters.createdTo);
  const res = await get<VendorPurchaseOrderListResponse>(`/vendor-purchase-orders?${query.toString()}`);
  return res.data;
}

export async function fetchVendorPurchaseOrderById(id: string) {
  const res = await get<{ success: boolean; data: VendorPurchaseOrderDto }>(`/vendor-purchase-orders/${id}`);
  return res.data;
}

export async function deleteVendorPurchaseOrder(id: string) {
  const res = await deleteRequest<{ success: boolean; message?: string }>(`/vendor-purchase-orders/${id}`);
  return res;
}

/**
 * Copy for a refused delete of a PO — alone, or with its vendor (D81): a PO
 * sent/amended or on an invoice is kept (VENDOR_PO_DELETE_LOCKED, naming each
 * PO and why from `validationErrors`). Chosen by `code` (invariant 6);
 * anything else reads `fallback`.
 */
export function deletePurchaseOrderErrorMessage(err: unknown, fallback: string): string {
  const e = err as { code?: unknown; validationErrors?: unknown } | null;
  switch (e?.code) {
    case 'VENDOR_PO_DELETE_LOCKED': {
      const why = lockReasons(e.validationErrors);
      return `Not deleted${why ? `: ${why}` : ''}. A purchase order the supplier has, or an invoice points at, is kept.`;
    }
    case 'VENDOR_PO_NOT_FOUND':
      return 'This purchase order could not be found. It may have been deleted already — reload and try again.';
    default:
      return fallback;
  }
}

// ─── Buy-to-order: draw on stock we already hold before buying ───────────────

/** One batch of stock on the shelf, offered against an order line. */
export interface ProposalLayer {
  layerId: string;
  quantityLevel1: number;
  quantityInOrderUom: number;
  expiryDate: string | null;
  remainingDays: number | null;
  receivedAt: string;
  costPerLevel1: number;
  currency: string;
  sourceInvoiceNumber: string;
  sourceVendorName: string;
  originalVesselName: string;
  shelfLife: {
    ok: boolean;
    severity: 'none' | 'expired' | 'short_dated' | 'unknown_expiry';
    remainingDays: number | null;
    shortfallDays: number | null;
    message: string;
  };
}

/**
 * One inventory item the line could be filled from (the match or an alternative),
 * with ITS OWN stock — choosing it in the modal must read these, not the match's.
 */
export interface ProposalCandidate {
  inventoryItemId: string;
  itemName: string;
  sku: string;
  score: number;
  /** On the shelf for THIS item, in the line's order unit. */
  availableInOrderUom: number;
  /** The draw to seed when the buyer picks this item (not gated by match confidence). */
  suggestedDraw: number;
  layers: ProposalLayer[];
  /** Shelf-life warnings of this item's layers (each carries a layerId). */
  warnings: { code: string; message: string; layerId?: string }[];
}

export interface ProposalLine {
  lineNumber: number | null;
  itemName: string;
  itemDescription: string;
  uom: string;
  orderedQuantity: number;
  match: (ProposalCandidate & { confident: boolean }) | null;
  alternatives: ProposalCandidate[];
  availableInOrderUom: number;
  proposedFromStock: number;
  /** The line's STORED pantry draw, in its order unit (0 when none) — netted off like the stock draw. */
  qtyFromPantry: number;
  quantityToBuy: number;
  layers: ProposalLayer[];
  shelfLifeRequirement: { minDays: number; kind: 'explicit' | 'long'; months: number; raw: string } | null;
  warnings: { code: string; message: string; layerId?: string }[];
  /**
   * Null, never 0, when there is stock to draw on but D54 has not left this line
   * a price yet (no supplier picked — TECH_DEBT #342). A real 0 means "nothing
   * drawn from stock", which is a different fact and must stay distinguishable
   * from "we don't know what this draw would have saved".
   */
  estimatedSaving: number | null;
}

export interface ProcurementProposal {
  confirmedOrderId: string;
  vesselName: string;
  currency: string;
  lines: ProposalLine[];
  summary: {
    linesTotal: number;
    linesWithStock: number;
    linesFullyCovered: number;
    linesNeedingConfirmation: number;
    /** Sum of the lines whose saving IS known — an unknown line is left out, not zeroed. */
    estimatedSaving: number;
    /** How many lines with stock to draw on have no price to value the saving at. */
    linesWithUnknownSaving: number;
  };
}

/** What the buyer confirmed to take off the shelf, per line. */
export interface StockAllocation {
  lineNumber: number;
  inventoryItemId?: string;
  qtyFromStock: number;
}

/**
 * Dry run: what could be filled from stock we already own, before any PO is
 * raised. Creates nothing and reserves nothing.
 */
export async function fetchProcurementProposal(confirmedOrderId: string) {
  const res = await get<{ success: boolean; data: ProcurementProposal }>(
    `/vendor-purchase-orders/from-confirmed/${confirmedOrderId}/procurement-proposal`
  );
  return res.data;
}

/** One order line that got NO purchase order at all — blank or pseudo-vendor `selectedVendor`. */
export interface MissingVendorLine {
  index: number;
  itemName: string;
  currentValue: string | null;
}

/** One PO that WAS raised (a real, typed supplier name) but never resolved to a Vendor record. */
export interface UnresolvedVendorPo {
  poId: string;
  poNumber: string;
  vendorName: string;
}

export interface CreatePosResult {
  data: VendorPurchaseOrderDto[];
  // `unresolvedVendors` is a sibling of `missingVendors`, not a replacement:
  // a blank/pseudo supplier line never got a PO (missingVendors); this is a
  // typed supplier that DID get a PO, but it named nobody in the vendor
  // register, so the PO has no vendorId/vendorEmail yet.
  warnings?: { pantry?: any[]; missingVendors?: MissingVendorLine[]; unresolvedVendors?: UnresolvedVendorPo[] };
  stock?: {
    coveredFromStock: {
      lineNumber: number | null;
      itemName: string;
      /** Null when the order line stores no quantity — never an invented 0. */
      orderedQuantity: number | null;
      fromStock: number;
      fromPantry: number;
      remainingToBuy: number;
      uom: string;
    }[];
    draw: { applied: any[]; skipped: any[] } | null;
  };
}

/**
 * Generate the vendor POs.
 *
 * `stockAllocations` is the buyer's confirmed draw from our own stock. Passing
 * it shrinks each line's vendor quantity by what came off the shelf, and drops
 * lines covered entirely. Omitting it generates exactly as it always did.
 */
export async function createVendorPurchaseOrdersFromConfirmed(
  confirmedOrderId: string,
  options?: { regenerate?: boolean; stockAllocations?: StockAllocation[] }
) {
  const q = options?.regenerate ? '?regenerate=1' : '';
  const body = options?.stockAllocations?.length
    ? { stockAllocations: options.stockAllocations }
    : {};
  const res = await postJson<CreatePosResult & { success: boolean }>(
    `/vendor-purchase-orders/from-confirmed/${confirmedOrderId}${q}`,
    body
  );
  return res.data;
}

/**
 * Copy for a failed PO generation, chosen by the envelope's `code` and never by
 * matching the message beside it (CLAUDE.md invariant 6).
 *
 * TECH_DEBT #211. `ConfirmedOrdersPage` used to `alert(e?.message)` here, which
 * meant the two useful refusals — "assign vendors first" and "nothing here is
 * orderable" — arrived as whatever sentence the service happened to carry, and
 * a copy edit on the server would have changed what the user was told. They now
 * have codes of their own, so the two can finally say different things: one is
 * a job for the user, the other is not.
 */
/**
 * D82 — the one client wording for "this PO is priced before VAT", shown only on
 * a PO with `priceBasis === 'before_vat'`. The same words as the server's
 * `vendorPurchaseOrderExport.service` PRICES_EXCLUDE_VAT, which the supplier
 * reads on the PDF/Excel (pinned equal by vendorPurchaseOrderApi.test.ts).
 */
export const PRICES_EXCLUDE_VAT = 'Prices exclude VAT';

/**
 * The one wording for "this total leaves lines out" (TECH_DEBT #342) — the
 * server's `vendorPurchaseOrderExport.service.totalPriceLine` says the exact
 * same sentence on the PDF/Excel a supplier reads and in the PO send email
 * (`vendorPoEmail.service.unpricedLineNote`). Every client screen that
 * shows a PO's (or several POs') totalPrice calls this rather than composing
 * its own note, so a buyer reading the app never sees different words for the
 * same fact the supplier's copy states.
 */
export function unpricedLineNote(unpricedLineCount: number): string {
  const n = Number(unpricedLineCount) || 0;
  if (n <= 0) return '';
  return `excludes ${n} line${n === 1 ? '' : 's'} with no agreed price`;
}

/**
 * The one wording for "these order lines got no purchase order at all" — no supplier was
 * chosen, so nothing was ever raised to buy them, and the gap is otherwise silent (the
 * defect this whole warning exists to close). Used both right after PO generation
 * (`ConfirmedOrderDetailPage`'s toast) and in the persistent banner on the same page that
 * reads `missingVendorLines` off the confirmed-order detail — one sentence, so the two
 * moments can never say different things about the same lines.
 */
export function missingVendorLinesNote(missingVendors: MissingVendorLine[]): string {
  const n = missingVendors.length;
  if (n <= 0) return '';
  const names = missingVendors.map((m) => m.itemName).filter(Boolean).join(', ');
  return `${n} order line${n === 1 ? '' : 's'} got no purchase order — no supplier chosen: ${names}.`;
}

/**
 * The one wording for "these purchase orders named a supplier that isn't in the vendor
 * register" — used in the toast right after generation. The persistent banner on
 * `VendorPurchaseOrdersPage.tsx` says the same fact in its own, shorter words (it is a
 * running list-page count, not a one-off notice about what generation just did), so it is
 * NOT this function — see that page for its wording.
 */
export function unresolvedVendorsGenerationNote(count: number): string {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return `${n} purchase order${n === 1 ? '' : 's'} name a supplier that is not in your vendor register — set one before sending.`;
}

// D82: generation, a vendor assigned later and the round endpoint take VAT out
// of the quotation cost at the Settings rate, and refuse without one. One sentence.
const VAT_RATE_NOT_SET_MESSAGE = 'Set the VAT rate in Settings first.';
// D102.7: with no TT$/US$ rate every conversion is refused.
const FX_RATE_NOT_SET_MESSAGE = 'Set the TT$/US$ rate in Settings first.';

// D81: the server lists each locked PO as {poId, poNumber, reasons}.
const PO_LOCK_REASONS: Record<string, string> = {
  WITH_SUPPLIER: 'has been sent to the supplier',
  ON_VENDOR_INVOICE: 'is on a vendor invoice',
  ON_CLIENT_INVOICE: 'is on a client invoice',
};

function lockReasons(rows: unknown): string {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const { poNumber, reasons } = (row || {}) as { poNumber?: unknown; reasons?: unknown };
      const said = (Array.isArray(reasons) ? reasons : [])
        .map((r) => PO_LOCK_REASONS[String(r)])
        .filter(Boolean);
      return typeof poNumber === 'string' && poNumber && said.length ? `${poNumber} ${said.join(' and ')}` : '';
    })
    .filter(Boolean)
    .join('; ');
}

export function createVendorPurchaseOrdersErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'VAT_RATE_NOT_SET':
      return VAT_RATE_NOT_SET_MESSAGE;
    case 'FX_RATE_NOT_SET':
      return FX_RATE_NOT_SET_MESSAGE;
    case 'PO_NO_VENDORS_ASSIGNED':
      return 'No vendor is assigned to any line on this order, so there is nothing to send a purchase order to. Open the order and choose a supplier for each line first.';
    case 'PO_NO_ELIGIBLE_ITEMS':
      return 'None of the lines on this order need a purchase order — they may already be covered from stock or already ordered.';
    case 'CONFIRMED_ORDER_NOT_FOUND':
      return 'This confirmed order could not be found. It may have been deleted — reload the page and try again.';
    // D81 — refused before anything is reserved or written.
    case 'ORDER_AWAITING_SHIP_REVIEW':
      return "The client's change or ship's PO on this order has not been reviewed yet — review it on the order before generating purchase orders.";
    // D81 — Regenerate would delete a PO the supplier has, or one an invoice points at.
    case 'VENDOR_PO_REGENERATE_LOCKED': {
      const why = lockReasons((err as { validationErrors?: unknown } | null)?.validationErrors);
      return `These purchase orders cannot be regenerated${why ? `: ${why}` : ''}. Change the order's quantities instead — its purchase orders follow them.`;
    }
    case 'QUOTATION_NOT_FOUND_FOR_CONFIRMED_ORDER':
      return 'The quotation behind this confirmed order could not be found, so its lines cannot be priced into a purchase order. Contact support before retrying.';
    // `INTERNAL_ERROR` is what the CENTRAL error handler stamps on anything
    // unexpected. Since TECH_DEBT #223 this controller has no catch of its own,
    // so that is the code an unforeseen failure arrives with;
    // `VENDOR_PO_CREATE_FAILED` is kept because a deployed server may still be
    // sending it while a release rolls out.
    case 'VENDOR_PO_CREATE_FAILED':
    case 'INTERNAL_ERROR':
      return 'The purchase orders could not be created. Nothing has been sent to any vendor. Please try again, and contact support if it keeps failing.';
    default:
      return 'The purchase orders could not be created. Please try again, and contact support if it keeps failing.';
  }
}

/** Same call, but keeps the warnings and stock summary the server returns. */
export async function createVendorPurchaseOrdersFromConfirmedDetailed(
  confirmedOrderId: string,
  options?: { regenerate?: boolean; stockAllocations?: StockAllocation[] }
) {
  const q = options?.regenerate ? '?regenerate=1' : '';
  const body = options?.stockAllocations?.length
    ? { stockAllocations: options.stockAllocations }
    : {};
  const result = await postJson<CreatePosResult & { success: boolean }>(
    `/vendor-purchase-orders/from-confirmed/${confirmedOrderId}${q}`,
    body
  );
  // The order page keeps its URL, so the sidebar trail would keep showing the step as locked.
  notifyTrailChanged();
  return result;
}

/** D119.7: the longest general remarks the server stores (VendorPurchaseOrder.GENERAL_REMARKS_MAX). */
export const GENERAL_REMARKS_MAX = 1000;

export interface UpdateVendorPurchaseOrderPayload {
  poNumber: string;
  vesselName?: string;
  hideVesselPrefix?: boolean;
  generalRemarks?: string;
  // No vendor name or contact: the vendor is picked (assignVendorToPurchaseOrder, D86); the server refuses them.
  createdAt?: string;
  expectedDeliveryDate?: string | null;
  items: Array<{
    lineNumber?: number;
    // No `itemName`: the order's wording is the order's; the PUT never reads it (D119.8).
    /** D119.8: the name the supplier sees (the PO page's name box). '' clears it - the line prints the order's wording. */
    supplierItemName?: string;
    itemDescription?: string;
    itemCode?: string;
    quantity: number | null;
    uom?: string;
    unitPrice: number | null;
    totalPrice: number | null;
    // No currency and no US$ snapshot: a PO line is TT$, and the snapshot is
    // server-owned (the PUT keeps the stored value, keepServerOwnedLineFields).
    remarks?: string;
  }>;
}

export async function updateVendorPurchaseOrder(id: string, payload: UpdateVendorPurchaseOrderPayload) {
  const res = await put<{ success: boolean; data: VendorPurchaseOrderDto }>(`/vendor-purchase-orders/${id}`, payload);
  return res.data;
}

/**
 * Copy for a failed `updateVendorPurchaseOrder` (the PO page's save), chosen by
 * `code` (CLAUDE.md invariant 6). Anything unmapped keeps the page's short
 * "Save failed".
 */
export function updateVendorPurchaseOrderErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  switch (code) {
    // D82: a pack line's unit is the supplier's pack, and a pending line is
    // answered only by the case-quantity choice under it (the round route).
    case 'VENDOR_PO_PACK_LINE_LOCKED':
      return "Not saved: a line in the supplier's pack keeps its unit, and a line waiting for a case quantity is answered with the choice under it.";
    case 'VENDOR_PO_LINES_MISMATCH':
      return 'Not saved: the lines on this purchase order have changed since it was opened. Reload the page and make the edit again.';
    // D82: a line in the supplier's pack is ordered in whole packs (4.5 / 0 refused).
    case 'VENDOR_PO_ROUNDING_QUANTITY_REQUIRED':
      return "Not saved: a line in the supplier's pack is ordered in whole cases. Enter a whole number of cases above 0.";
    // The sign rule (`poLineAmountProblem`): the page refuses these first; this is the server's answer
    // to a request that got past it.
    case 'VENDOR_PO_QUANTITY_INVALID':
      return 'Not saved: a quantity must be above 0. Fix the highlighted line and save again.';
    case 'VENDOR_PO_PRICE_INVALID':
      return 'Not saved: a price cannot be negative. Fix the highlighted line and save again.';
    // D81: a line whose order line left after the PO was sent stays at 0.
    case 'VENDOR_PO_LINE_REMOVED':
      return 'Not saved: a line removed from the order stays at 0 on the purchase order. Change quantities on the confirmed order — the purchase order follows it.';
    // D119.7: the box keeps whatever is typed or pasted (a silent cut would send the supplier half a
    // sentence), so this is THE answer to a note past the limit; the page's counter turns red first.
    case 'VENDOR_PO_GENERAL_REMARKS_TOO_LONG':
      return `Not saved: general remarks are limited to ${GENERAL_REMARKS_MAX} characters. Shorten them and save again.`;
    case 'VENDOR_PO_GENERAL_REMARKS_INVALID':
      return 'Not saved: general remarks must be text. Reload the page and type them again.';
    // D170.3: the supplier holds a sent PO under its number.
    case 'VENDOR_PO_NUMBER_LOCKED':
      return 'Not saved: a purchase order already sent to the supplier keeps its number.';
    // D157.10: a save re-costs the client invoices its lines cost, which can need the rate.
    case 'FX_RATE_NOT_SET':
      return FX_RATE_NOT_SET_MESSAGE;
    default:
      return 'Save failed';
  }
}

/**
 * Fix-in-place for a PO with no vendor record: point it at a real, existing Vendor.
 * Dedicated endpoint — NOT `updateVendorPurchaseOrder`/`PUT /:id` — the server looks the
 * vendor up by id and writes vendorId/vendorName/vendorEmail from THAT record; nothing
 * this call sends is trusted for those fields beyond the id itself. Refused with
 * `VENDOR_PO_ALREADY_SENT` if the PO has already been sent.
 *
 * `notes`: what the assign did NOT do, as codes from the answer's `meta.notes`
 * (D82: a before-VAT PO whose confirmed order is gone gets the vendor only —
 * `VENDOR_PO_LINES_NOT_REBUILT` / `CONFIRMED_ORDER_NOT_FOUND`). Worded for a
 * person by `assignVendorNotesMessage`.
 */
export interface AssignVendorNote {
  code: string;
  reason?: string | null;
  /** SOURCE_NOT_FOUND: the stored line numbers of the lines left as they were (null = none stored). */
  lineNumbers?: Array<number | null> | null;
}

export async function assignVendorToPurchaseOrder(id: string, vendorId: string) {
  const res = await patch<{
    success: boolean;
    data: VendorPurchaseOrderDto;
    meta?: { notes?: AssignVendorNote[] };
  }>(`/vendor-purchase-orders/${id}/vendor`, { vendorId });
  return { purchaseOrder: res.data, notes: res.meta?.notes ?? [] };
}

const ASSIGN_VENDOR_NOTE_WHAT: Record<string, string> = {
  VENDOR_PO_LINES_NOT_REBUILT: 'its lines were not rebuilt for this vendor',
};
const ASSIGN_VENDOR_NOTE_WHY: Record<string, string> = {
  CONFIRMED_ORDER_NOT_FOUND: 'the confirmed order it was raised from could not be found',
  SOURCE_NOT_FOUND: 'they could not be traced to a line of the confirmed order (added by hand, or the order changed since)',
};

/**
 * The one wording for what a vendor assign did not do, chosen by code
 * (invariant 6), or null when it did everything. A note naming lines
 * (`lineNumbers`, SOURCE_NOT_FOUND) says which: "lines 3, 7", or a count
 * when some have no stored number.
 */
export function assignVendorNotesMessage(notes: AssignVendorNote[]): string | null {
  if (!notes.length) return null;
  const each = notes.map((n) => {
    const lines = n.lineNumbers ?? [];
    const one = lines.length === 1;
    const which = lines.every((x) => x != null)
      ? `${one ? 'line' : 'lines'} ${lines.join(', ')}`
      : `${lines.length} ${one ? 'line' : 'lines'}`;
    const what = n.code === 'VENDOR_PO_LINES_NOT_REBUILT' && lines.length
      ? `${which} ${one ? 'was' : 'were'} not rebuilt for this vendor`
      : ASSIGN_VENDOR_NOTE_WHAT[n.code] ?? 'not everything on it was updated';
    const why = n.reason ? ASSIGN_VENDOR_NOTE_WHY[n.reason] : undefined;
    return why ? `${what}: ${why}` : what;
  });
  return `The vendor was set, but ${each.join('; ')}. Anything not rebuilt keeps the pack and price it was raised with - check it before sending.`;
}

/**
 * Copy for a failed `assignVendorToPurchaseOrder` call, chosen by the envelope's `code`
 * (CLAUDE.md invariant 6) — same reasoning as `createVendorPurchaseOrdersErrorMessage`.
 */
export function assignVendorToPurchaseOrderErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  switch (code) {
    case 'VENDOR_PO_ALREADY_SENT':
      return 'This purchase order has already been sent, so its vendor can no longer be changed here.';
    case 'VENDOR_PO_VENDOR_ALREADY_SET':
      return 'This purchase order was raised for a vendor, so its vendor cannot be changed.';
    case 'VENDOR_PO_CHANGED':
      return 'This purchase order was changed while the vendor was being set — reload it to see its vendor.';
    case 'VENDOR_NOT_FOUND':
      return 'That vendor could not be found. It may have been deleted — reload and try again.';
    case 'VENDOR_PO_VENDOR_ID_REQUIRED':
      return 'Choose a vendor first.';
    case 'VENDOR_PO_NOT_FOUND':
      return 'This purchase order could not be found. It may have been deleted — reload and try again.';
    // D82: assigning a vendor rebuilds the lines from the confirmed order, priced before VAT.
    case 'VAT_RATE_NOT_SET':
      return VAT_RATE_NOT_SET_MESSAGE;
    case 'FX_RATE_NOT_SET':
      return FX_RATE_NOT_SET_MESSAGE;
    // Reachable only in a race: the assign checks the order exists first and,
    // when it is gone, sets the vendor and answers a note instead
    // (`assignVendorNotesMessage`); this is the order deleted between that
    // check and the rebuild reading it.
    case 'CONFIRMED_ORDER_NOT_FOUND':
      return 'The confirmed order this purchase order was raised from could not be found, so its lines cannot be rebuilt for this vendor.';
    default:
      return 'The vendor could not be set. Please try again, and contact support if it keeps failing.';
  }
}

/**
 * Answer a PENDING pack line (D82): order `up` (ceil of pieces / pack), `down`
 * (floor) or `manual` (that many packs). `lineIndex` is the line's index in the
 * PO's stored `items`. Returns the whole PO, re-priced by the server.
 */
export type PackRoundingBody =
  | { decision: 'up' }
  | { decision: 'down' }
  | { decision: 'manual'; quantity: number };

export async function decidePackRounding(poId: string, lineIndex: number, body: PackRoundingBody) {
  const res = await patch<{ success: boolean; data: VendorPurchaseOrderDto }>(
    `/vendor-purchase-orders/${poId}/items/${lineIndex}/round`,
    body,
  );
  return res.data;
}

/** Copy for a failed `decidePackRounding`, chosen by `code` (CLAUDE.md invariant 6). */
export function decidePackRoundingErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  switch (code) {
    case 'VENDOR_PO_LINE_NOT_PENDING':
      return 'This line has already been answered. Reload the page to see its current quantity.';
    case 'VENDOR_PO_ROUNDING_QUANTITY_REQUIRED':
      return 'Enter a number of cases greater than 0.';
    case 'VENDOR_PO_ROUNDED_TO_ZERO':
      return 'That would order 0 cases. Choose at least 1.';
    // The order line this PO line was raised from is gone, so the server cannot
    // price the cases. A hand edit cannot answer it either (the PUT refuses a
    // pending line, VENDOR_PO_PACK_LINE_LOCKED), so the way out is a fresh PO.
    // A confirmed order that is gone (traceLinesToOrder) is the same dead end.
    case 'VENDOR_PO_LINE_SOURCE_NOT_FOUND':
    case 'CONFIRMED_ORDER_NOT_FOUND':
      // D81: Regenerate is refused once any PO of the order is sent/amended or
      // on an invoice (VENDOR_PO_REGENERATE_LOCKED) — offered only for drafts.
      return "The order line this came from can no longer be found, so the case price cannot be worked out. If the order's purchase orders are all still a draft and not on any invoice, regenerate them from the confirmed order; otherwise contact support.";
    // The round endpoint restates the case price before VAT (D82) and needs the rate.
    case 'VAT_RATE_NOT_SET':
      return VAT_RATE_NOT_SET_MESSAGE;
    case 'FX_RATE_NOT_SET':
      return FX_RATE_NOT_SET_MESSAGE;
    // D170.3: a draft, sent or amended PO takes a pack decision; a billed or cancelled one does not.
    case 'VENDOR_PO_NOT_DRAFT':
      return 'This purchase order is billed or cancelled, so its quantities cannot be changed here.';
    case 'VENDOR_PO_NOT_FOUND':
      return 'This purchase order could not be found. It may have been deleted — reload and try again.';
    default:
      return 'The case quantity could not be saved. Please try again, and contact support if it keeps failing.';
  }
}

/**
 * "Convert all to cases" (D157.8): the saved PO, the lines converted and those left with a `reason` code.
 * Refused like a pack decision: word a failure with `decidePackRoundingErrorMessage`.
 */
export interface ConvertToCasesLine {
  /** Position on the PO (0-based): the line the screen numbers `lineIndex + 1`. `lineNumber` is the ORDER's line number. */
  lineIndex: number;
  lineNumber: number | null;
  itemName?: string;
  /** A converted line: what was needed, the pack it went into, and how many pieces over the whole packs bring. */
  requested?: number;
  requestedUom?: string;
  packs?: number;
  packUnit?: string;
  piecesPerPack?: number;
  extra?: number;
  source?: 'history' | 'catalogue' | null;
}
/**
 * A line left, with why. D169.7: a catalogue part case also says the case size (`packUnit`, `piecesPerPack`, in the
 * line's `uom`) and what it asked (`requested`); one above a case (`NOT_WHOLE_CASES`) adds the whole cases it rounds up
 * to (`packs`) and the `extra` they bring, which staff confirm with `roundUp`.
 */
export interface ConvertToCasesSkippedLine extends ConvertToCasesLine {
  uom: string;
  reason: string;
}

/** A round-up staff confirmed: line `lineIndex` (position on the PO) in exactly `packs` whole cases. */
export interface ConvertToCasesRoundUp {
  lineIndex: number;
  packs: number;
}

export async function convertPurchaseOrderToCases(poId: string, roundUp?: ConvertToCasesRoundUp[]) {
  const res = await postJson<{
    success: boolean;
    data: VendorPurchaseOrderDto;
    meta?: { converted?: ConvertToCasesLine[]; skipped?: ConvertToCasesSkippedLine[] };
  }>(`/vendor-purchase-orders/${poId}/convert-to-cases`, roundUp ? { roundUp } : {});
  return { purchaseOrder: res.data, converted: res.meta?.converted ?? [], skipped: res.meta?.skipped ?? [] };
}

/** The round-ups the server offered (D169.7): each part case above one case, at the count it offered. */
export function convertToCasesRoundUpOffers(skipped: ConvertToCasesSkippedLine[]): ConvertToCasesRoundUp[] {
  return skipped
    .filter((s) => s.reason === 'NOT_WHOLE_CASES' && s.packs != null && s.packs > 0)
    .map((s) => ({ lineIndex: s.lineIndex, packs: s.packs as number }));
}

const lineLabel = (l: ConvertToCasesLine) => `Line ${l.lineIndex + 1}${l.itemName ? ` (${l.itemName})` : ''}`;
/** A case size on the quantity grid: 20 LBS a case is 9.049774 KG, not 9.049773755656108. */
const onGrid = (n: number) => roundQuantity(n) ?? n;

/** "a CASE holds 10 KG, more than the 5 KG on this line; raise it to at least 10 KG to order it in cases". Null without the detail. */
function lessThanOneCaseText(s: ConvertToCasesSkippedLine): string | null {
  if (s.piecesPerPack == null || s.requested == null) return null;
  const u = s.uom ? ` ${s.uom}` : '';
  const perCase = onGrid(s.piecesPerPack);
  return `a ${s.packUnit || 'CASE'} holds ${perCase}${u}, more than the ${s.requested}${u} on this line; raise it to at least ${perCase}${u} to order it in cases`;
}

/** "12.5 KG -> 2 CASE of 10 KG (20 KG); 7.5 KG extra go to stock". Null without the detail. */
function roundUpText(s: ConvertToCasesSkippedLine): string | null {
  if (s.piecesPerPack == null || s.requested == null || s.packs == null) return null;
  const u = s.uom ? ` ${s.uom}` : '';
  const ordered = onGrid(s.packs * s.piecesPerPack);
  return `${s.requested}${u} -> ${s.packs} ${s.packUnit || 'CASE'} of ${onGrid(s.piecesPerPack)}${u} (${ordered}${u}); ${s.extra ?? 0}${u} extra go to stock`;
}

/**
 * What "Convert all to cases" asks staff after it ran (D169.7), or null when nothing is waiting on them: the round-ups
 * to confirm (12.5 KG -> 2 CASE, the extra to stock), and the lines under one case to raise. With round-ups it is a
 * confirm (its lines are what `convertToCasesRoundUpOffers` sends); with only short lines, a notice.
 */
export function convertToCasesDialog(skipped: ConvertToCasesSkippedLine[]): DialogContent | null {
  const offers = skipped
    .filter((s) => s.reason === 'NOT_WHOLE_CASES')
    .map((s) => ({ s, text: roundUpText(s) }))
    .filter((o): o is { s: ConvertToCasesSkippedLine; text: string } => o.text != null);
  const short = skipped
    .filter((s) => s.reason === 'LESS_THAN_ONE_CASE')
    .map((s) => ({ s, text: lessThanOneCaseText(s) }))
    .filter((o): o is { s: ConvertToCasesSkippedLine; text: string } => o.text != null);
  const asItem = ({ s, text }: { s: ConvertToCasesSkippedLine; text: string }) =>
    ({ label: `Line ${s.lineIndex + 1}`, text, note: s.itemName || undefined });
  if (offers.length) {
    return {
      tone: 'confirm',
      title: 'Round up to whole cases?',
      summary: `${offers.length === 1 ? 'This line is' : 'These lines are'} not a whole number of cases. Order the whole cases below instead? The supplier sends full cases; what the vessel did not ask for goes to stock.`,
      items: offers.map(asItem),
      hint: short.length
        ? `Less than one case, not converted: ${short.map(({ s, text }) => `${lineLabel(s)}: ${text}`).join('. ')}.`
        : undefined,
    };
  }
  if (!short.length) return null;
  return {
    tone: 'blocked',
    title: 'Less than one case',
    summary: `${short.length === 1 ? 'This line is' : 'These lines are'} smaller than one case, so ${short.length === 1 ? 'it was' : 'they were'} not converted.`,
    items: short.map(asItem),
    hint: 'Increase the quantity to at least one case, save, and convert again.',
  };
}

const CONVERT_TO_CASES_SKIP_WHY: Record<string, string> = {
  NO_PACK_KNOWN: 'no case size on file for this supplier or in the master catalogue',
  NOT_SINGLE_ITEMS: 'not in single items',
  // D169.7: a catalogue part case above one case, until staff confirm its round-up.
  NOT_WHOLE_CASES: 'not a whole number of cases',
  // D169.7: under one case; the line asks staff to raise it.
  LESS_THAN_ONE_CASE: 'less than one case',
  NO_QUANTITY: 'no quantity to convert',
  SOURCE_NOT_FOUND: 'its order line can no longer be found, so the case price cannot be worked out',
  ALREADY_IN_PACK: "already in the supplier's pack",
};

/**
 * The one wording for what "Convert all to cases" did, chosen by code: the lines converted (with the case size used, where it
 * came from, and any round-up: "need 8 PCS, a CASE holds 12 PCS, 4 PCS extra go to stock"), then each line left, named by
 * its position on this PO and its item with the reason ("Line 1 (Flour tortillas) stays in PACK: no case size on file ...").
 */
export function convertToCasesResultMessage(converted: ConvertToCasesLine[], skipped: ConvertToCasesSkippedLine[]): string {
  const lines = (n: number) => `${n} line${n === 1 ? '' : 's'}`;
  const which = lineLabel;
  const head = converted.length ? `${lines(converted.length)} converted to whole cases.` : 'No line was converted.';
  const done = converted.map((l) => {
    if (!l.packs || !l.piecesPerPack || l.requested == null) return '';
    const from = l.source === 'catalogue' ? 'the master catalogue' : l.source === 'history' ? "this supplier's invoices" : '';
    // The unit asked in, so a weight or a pack reads right: "a CASE holds 10 KG, so 5 KG extra".
    const u = l.requestedUom ? ` ${l.requestedUom}` : '';
    const perCase = onGrid(l.piecesPerPack);
    const base = `${which(l)}: ${l.packs} ${l.packUnit} of ${perCase}${u}${from ? ` (case size from ${from})` : ''}`;
    return l.extra && l.extra > 0
      ? `${base}; you need ${l.requested}${u}, a ${l.packUnit} holds ${perCase}${u}, so ${l.extra}${u} extra go to stock.`
      : `${base}.`;
  }).filter(Boolean);
  const left = skipped.map((s) => {
    const detailed = s.reason === 'LESS_THAN_ONE_CASE' ? lessThanOneCaseText(s) : null;
    const why = detailed ?? CONVERT_TO_CASES_SKIP_WHY[s.reason] ?? 'left as it is';
    return `${which(s)} stays${s.uom ? ` in ${s.uom}` : ''}: ${why}.`;
  });
  return [head, ...done, ...left].join(' ');
}

/**
 * The one wording for "a line is still waiting for a case quantity, so this PO
 * cannot go to the supplier" (D82) — beside a disabled Send/Export (count known)
 * and for the server's `VENDOR_PO_PACK_DECISION_PENDING` refusal (count unknown).
 */
export function packDecisionPendingNote(lineCount: number | null): string {
  if (lineCount == null) {
    return 'A line needs a case quantity before this can be sent or exported. Open the purchase order and choose one.';
  }
  return `${lineCount} line${lineCount === 1 ? ' needs' : 's need'} a case quantity before this can be sent or exported.`;
}

/**
 * Copy for a failed send / send-batch / preview, by `code` where this knows it;
 * anything else keeps the message or the caller's fallback (D180).
 */
export function vendorPoSendErrorMessage(err: unknown, fallback: string): string {
  const refusal = emailRefusalMessage(err);
  if (refusal) return refusal;
  const e = err as { code?: unknown; message?: unknown; validationErrors?: unknown } | null;
  if (e?.code === 'VENDOR_PO_PACK_DECISION_PENDING') {
    // The server lists the refused POs as [{poId, poNumber, lineNumbers}].
    const poNumbers = (Array.isArray(e.validationErrors) ? e.validationErrors : [])
      .map((row) => (row as { poNumber?: unknown } | null)?.poNumber)
      .filter((n): n is string => typeof n === 'string' && n !== '');
    if (poNumbers.length === 0) return packDecisionPendingNote(null);
    return poNumbers.length === 1
      ? `${poNumbers[0]} needs a case quantity on a line before it can be sent or exported. Open it and choose one.`
      : `${poNumbers.join(', ')} need a case quantity on a line before they can be sent or exported. Open each and choose one.`;
  }
  // D81: a draft whose every line left the order is cancelled, never sent.
  if (e?.code === 'VENDOR_PO_CANCELLED') {
    return 'This purchase order is cancelled — every line on it left the order, so there is nothing to send.';
  }
  // B027: the server refuses a PO with no lines.
  if (e?.code === 'VENDOR_PO_NO_LINES') {
    return 'This purchase order has no lines to send.';
  }
  // D180: the mail went; sending again would mail the supplier twice.
  if (e?.code === 'VENDOR_PO_EMAILED_NOT_RECORDED') {
    return 'The email went to the vendor, but the purchase order could not be marked as sent. Do not send it again; reload the page and contact support if it still shows unsent.';
  }
  return typeof e?.message === 'string' && e.message ? e.message : fallback;
}

/**
 * A batch send that threw (D180). A 4xx is a refusal before any mail; anything else, a dropped connection included,
 * may have come after some emails went, so "nothing went" would be a guess.
 */
export function vendorPoBatchSendErrorMessage(err: unknown): string {
  const lost = 'The answer was lost, so some emails may have gone. Reload and check what is marked sent before sending again.';
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500 ? vendorPoSendErrorMessage(err, lost) : lost;
}

/** D180: the email a send screen starts from, as the server drafts it. */
export interface VendorPoEmailDraft {
  vendorName: string;
  poIds: string[];
  poNumbers: string[];
  /** Which state of these POs the text was drafted from; sent back with the email. */
  draftVersion: string;
  recipients: string[];
  subject: string;
  body: string;
}

export interface VendorPoSendRow {
  id: string;
  poNumber?: string;
  reason?: string;
}

export async function fetchVendorPoEmailDrafts(poIds: string[], resend = false) {
  const res = await postJson<{ success: boolean; data: { drafts: VendorPoEmailDraft[]; skipped: VendorPoSendRow[] } }>(
    `/vendor-purchase-orders/email-drafts`,
    { poIds, ...(resend ? { resend: true } : {}) },
  );
  return res.data;
}

/** "Purchase Order PO-1 (PDF)": the Attached line of a PO email. */
export function poAttachmentLabel(poNumbers: string[], format: 'pdf' | 'excel'): string {
  return `Purchase Order${poNumbers.length > 1 ? 's' : ''} ${poNumbers.join(', ')} (${format === 'excel' ? 'Excel' : 'PDF'})`;
}

const SEND_REASON_WORDS: Record<string, string> = {
  'already sent': 'already sent',
  cancelled: 'cancelled',
  'no lines': 'no lines to send',
  'not found': 'no longer exists',
  'email not sendable': 'its email also names a purchase order that can no longer go, so that email did not go',
  'changed since drafted': 'its email changed after the text was drafted, so that email did not go. Close this and open it again to see the new text',
  send_failed: 'the email could not be sent',
  vendors_differ: 'its email names purchase orders of different vendors',
};

/** Words for a `skipped[].reason` or `failed[].reason` of the PO send. */
export function vendorPoSendReasonWords(reason?: string): string {
  return (reason && SEND_REASON_WORDS[reason]) || 'not sent';
}

/** What a batch send did to anything other than plainly send it, one sentence per PO. */
export function vendorPoSendProblems(result: BatchSendResult): string[] {
  const label = (row: VendorPoSendRow) => row.poNumber || 'A purchase order';
  return [
    ...result.notRecorded.map((row) => `${label(row)}: the email went to the vendor, but it could not be marked as sent. Do not send it again.`),
    ...result.failed.map((row) => `${label(row)} was not sent: ${vendorPoSendReasonWords(row.reason)}.`),
    ...result.skipped.map((row) => `${label(row)} was not sent: ${vendorPoSendReasonWords(row.reason)}.`),
  ];
}

export interface SendVendorPurchaseOrderPayload {
  recipients: string[];
  subject: string;
  body: string;
  draftVersion: string;
  format: 'pdf' | 'excel';
  /** Only when the person saw that it was already sent. */
  resend?: true;
}

export async function sendVendorPurchaseOrder(id: string, payload: SendVendorPurchaseOrderPayload) {
  const res = await postJson<{ success: boolean; data: VendorPurchaseOrderDto }>(
    `/vendor-purchase-orders/${id}/send`,
    payload,
  );
  return res.data;
}

export interface BatchSendPayload {
  /** One entry per email on the screen; each goes exactly as given. */
  emails: { poIds: string[]; draftVersion: string; recipients: string[]; subject: string; body: string }[];
  format: 'pdf' | 'excel';
  /** Only when the person saw that a PO was already sent. */
  resend?: true;
}
export interface BatchSendResult {
  sent: { id: string; poNumber?: string; to?: string }[];
  skipped: VendorPoSendRow[];
  failed: VendorPoSendRow[];
  /** The email went, but the PO could not be marked sent. */
  notRecorded: { id: string; poNumber?: string; to?: string }[];
}
export async function sendVendorPurchaseOrdersBatch(payload: BatchSendPayload) {
  const res = await postJson<{ success: boolean; result: BatchSendResult }>(
    `/vendor-purchase-orders/send-batch`,
    payload,
  );
  return res.result;
}

export interface VendorPoSummaryItem {
  id: string;
  poNumber: string;
  totalPrice: number | null;
  /** How many of this PO's lines have no agreed price yet — TECH_DEBT #342. */
  unpricedLineCount: number;
  /** D82: 'before_vat' on a PO priced before VAT; null on one stored before D82. */
  priceBasis?: 'before_vat' | null;
  status: string;
  sentAt?: string | null;
}

export interface VendorPoSummary {
  vendorId: string | null;
  vendorName: string;
  vendorEmail: string;
  total: number;
  unsentCount: number;
  unsentPoIds: string[];
  sentCount: number;
  /** Summed across every PO in `pos` — TECH_DEBT #342. */
  unpricedLineCount: number;
  pos: VendorPoSummaryItem[];
}
export async function fetchVendorPoVendorSummary() {
  const res = await get<{ success: boolean; data: VendorPoSummary[] }>(
    `/vendor-purchase-orders/vendor-summary`,
  );
  return res.data;
}

export async function previewVendorPurchaseOrders(poIds: string[], format: 'pdf' | 'excel'): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}/vendor-purchase-orders/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ poIds, format }),
  });
  // A refusal (e.g. VENDOR_PO_PACK_DECISION_PENDING) must keep its `code`:
  // apiClient's one error path throws with it. It always throws on !ok.
  if (!res.ok) await handleResponse(res);
  return res.blob();
}

/**
 * One PO saved as the file its supplier gets: the same PDF / Excel the preview
 * builds (the server's one layout), named after the PO number. The list's
 * Export picker and the PO page's Export (D161.5) call this. A refusal throws
 * with its `code`, as the preview does.
 */
export async function downloadVendorPurchaseOrder(
  po: Pick<VendorPurchaseOrderDto, '_id' | 'poNumber'>,
  format: 'pdf' | 'excel',
): Promise<void> {
  const blob = await previewVendorPurchaseOrders([po._id], format);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${po.poNumber || 'purchase-order'}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
